import type { ImgHTMLAttributes } from "react";

export interface OptimizedPictureProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, "src"> {
  /** Full image path (e.g. /lovable-uploads/xxx.png). WebP/AVIF paths derived by replacing extension. */
  src: string;
  width: number;
  height: number;
  loading?: "lazy" | "eager";
  fetchPriority?: "high" | "low" | "auto";
  alt: string;
  className?: string;
}

/**
 * Renders <picture> with AVIF and WebP sources and PNG/JPG fallback for smaller payload and better LCP.
 */
export function OptimizedPicture({
  src,
  width,
  height,
  loading,
  fetchPriority,
  alt,
  className,
  decoding = "async",
  ...imgProps
}: OptimizedPictureProps) {
  const lastDot = src.lastIndexOf(".");
  const base = lastDot > 0 ? src.slice(0, lastDot) : src;
  const avifSrc = `${base}.avif`;
  const webpSrc = `${base}.webp`;
  const effectiveLoading = loading ?? (fetchPriority === "high" ? "eager" : "lazy");

  return (
    <picture>
      <source type="image/avif" srcSet={avifSrc} />
      <source type="image/webp" srcSet={webpSrc} />
      <img
        src={src}
        alt={alt}
        width={width}
        height={height}
        loading={effectiveLoading}
        decoding={decoding}
        fetchPriority={fetchPriority}
        className={className}
        {...imgProps}
      />
    </picture>
  );
}
